"use client";

import React, { useEffect, useState } from "react";
import { Mail, Phone, Menu, X, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205980" className="flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-white/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!Array.isArray(menuItems) || menuItems.length === 0) {
    return null;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground font-default font-medium text-base relative after:absolute after:bottom-[-4px] after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 hover:after:w-full py-1 outline-none">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-card border-t border-border/10 shadow-2xl lg:hidden flex flex-col z-50">
      <div className="flex flex-col px-4 py-4 gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-white/10 animate-pulse rounded-sm" />)}
          </div> : Array.isArray(menuItems) && menuItems.length > 0 ? menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/10 pb-2 last:border-0">
              <Link to={item.href || "#"} className="text-foreground font-default font-bold text-lg block w-full" onClick={onClose}>
                {item.label}
              </Link>
            </div>) : <div className="text-muted-foreground text-sm py-2">No hay opciones de menú disponibles</div>}
        
        <div className="flex flex-col gap-4 pt-4 border-t border-border/10 mt-2">
          <Button variant="default" asChild className="bg-primary text-primary-foreground font-default font-bold rounded-sm h-9 px-4 text-xs hover:bg-primary/90 transition-all shadow-md">
            <Link to="https://wa.me/34624990509" newTab onClick={onClose}>
              SOLICITAR DIAGNÓSTICO WHATSAPP
            </Link>
          </Button>

          <div className="flex items-center gap-2 text-foreground">
            <Globe className="size-5 text-primary" />
            <span className="font-default font-medium">Idioma:</span>
            <Select defaultValue="es">
              <SelectTrigger className="w-[120px] h-8 bg-secondary/50 border-none text-foreground focus:ring-1 focus:ring-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-col gap-2 text-muted-foreground text-sm mt-4">
            <span className="flex items-center gap-2"><Mail className="size-4 text-primary" /> info@bateriassevilla.com</span>
            <span className="flex items-center gap-2"><Phone className="size-4 text-primary" /> +34 624 990 509</span>
          </div>
        </div>
      </div>
    </div>;
}
export default function CabeceraPrincipal() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      try {
        setScrolled(window.scrollY > 20);
      } catch (error) {
        console.error("Error handling scroll:", error);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement;
        if (!target) return;
        const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
        if (anchor) {
          const href = anchor.getAttribute('href');
          if (!href) return;
          let hash = '';
          if (href.startsWith('#')) {
            hash = href;
          } else if (href.startsWith('/') && href.includes('#')) {
            const [path, hashPart] = href.split('#');
            if (path === '/' || path === window.location.pathname) {
              hash = '#' + hashPart;
            }
          }
          if (hash && hash !== '#') {
            const element = document.querySelector(hash);
            if (element && typeof element.scrollIntoView === 'function') {
              e.preventDefault();
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
              setMobileMenuOpen(false);
            }
          }
        }
      } catch (error) {
        console.error("Error handling anchor click:", error);
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className="dark bg-background text-foreground sticky top-0 z-50 flex flex-col w-full">
      
      <div className="bg-secondary w-full border-b border-border/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex justify-between items-center gap-2">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-foreground/80 text-[10px] sm:text-xs md:text-sm font-default font-light">
            <span className="flex items-center gap-1.5">
              <Mail className="size-3 md:size-3.5 text-primary" /> 
              info@bateriassevilla.com
            </span>
            <span className="flex items-center gap-1.5">
              <Phone className="size-3 md:size-3.5 text-primary" /> 
              +34 624 990 509
            </span>
            <span className="text-foreground/60 hidden sm:inline">Sevilla, Andalucía y España</span>
          </div>
          <Button variant="default" asChild className="bg-primary text-primary-foreground font-default font-bold rounded-sm h-7 md:h-8 px-3 md:px-6 text-[9px] md:text-xs hover:bg-primary/90 transition-all animate-pulse-subtle shrink-0">
            <Link to="https://wa.me/34624990509" newTab>
              <span className="hidden xs:inline">SOLICITAR DIAGNÓSTICO</span>
              <span className="xs:hidden">DIAGNÓSTICO</span>
            </Link>
          </Button>
        </div>
      </div>

      
      <div className={`w-full transition-all duration-300 relative ${scrolled ? "bg-background/80 backdrop-blur-lg py-3 shadow-xl" : "bg-background py-5"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center">
          
          <Link to="/" className="flex-shrink-0">
            <WvcLogo className="h-[7rem] sm:h-[5rem] w-auto max-w-full object-contain" />
          </Link>

          
          <div className="hidden lg:flex items-center gap-8">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
            
            <div className="flex items-center gap-6 border-l border-border/20 pl-6">
              <Select defaultValue="es">
                <SelectTrigger className="w-auto h-8 bg-transparent border-none text-foreground focus:ring-0 gap-1 px-2 hover:bg-white/5 rounded-sm cursor-pointer font-default text-sm">
                  <Globe className="size-4 text-primary" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">ES</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          
          <div className="lg:hidden flex items-center gap-5">
            <Button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} variant="ghost" size="icon" className="text-foreground hover:bg-white/5 rounded-sm lg:hidden" aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        
        <MenuProvider menu_id="16">
          <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}